/** Conjunction watchlist: Space-Track CDM or SOCRATES rows, fleet matches first (loader-conjunctions.js). */
import { useMemo } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  CONJUNCTION_ROW_LIMIT,
  formatConjunctionCountdown,
  missionNoradMap,
  orderConjunctions,
  pcClass,
  type PcClass,
} from '../../../awareness/conjunctions';
import { useAwareness } from '../../../awareness/store';
import { useLoader } from '../../../store';
import { ErrorLine, Muted } from './Section';
import { useNow } from './shared';

const PC_TONE: Record<PcClass, string> = {
  critical: 'text-warning',
  action: 'text-caution',
  watch: 'text-accent',
  low: 'text-ink-3',
};

export function ConjunctionsPanel() {
  const state = useAwareness((s) => s.conjunctions);
  const missions = useLoader((s) => s.dataset?.missions ?? null);
  const now = useNow(1000);
  const noradMap = useMemo(() => missionNoradMap(missions ?? []), [missions]);
  const ordered = useMemo(() => (state ? orderConjunctions(state.events, noradMap, now) : []), [state, noradMap, now]);

  if (!state) return <Muted>Loading watchlist.</Muted>;
  const hasMissions = Object.keys(noradMap).length > 0;
  const matched = ordered.filter((o) => o.involves).length;

  return (
    <div className="flex flex-col gap-2">
      {state.source === null ? (
        <ErrorLine text={`Watchlist feed unavailable${state.error ? `: ${state.error}` : ''}`} />
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 text-[10px] text-ink-3">
          <span className={state.source === 'space-track' ? 'text-nominal' : 'text-caution'}>
            {state.source === 'space-track' ? 'Space-Track CDM' : 'CelesTrak SOCRATES (fallback, no Pc)'}
          </span>
          <span>{state.events.length} events</span>
          {hasMissions && (
            <span className={matched > 0 ? 'text-accent' : undefined}>
              {matched > 0 ? `${matched} involve your fleet` : '0 involve your fleet'}
            </span>
          )}
        </div>
      )}
      {state.source !== null && ordered.length === 0 && <Muted>No upcoming close approaches in the next 7 days</Muted>}
      {ordered.length > 0 && (
        <ul className="flex flex-col gap-1">
          {ordered.slice(0, CONJUNCTION_ROW_LIMIT).map(({ event, involves }) => {
            const cls = pcClass(event.pc);
            const deltaSec = Math.floor((event.tcaMs - now) / 1000);
            return (
              <li
                key={`${event.norad1}-${event.norad2}-${event.tcaMs}`}
                className={cn(
                  'rounded-control border px-2.5 py-1.5 leading-snug',
                  cls === 'critical'
                    ? 'border-warning/50 bg-warning/5'
                    : cls === 'action'
                      ? 'border-caution/50 bg-caution/5'
                      : 'border-hairline',
                  involves && 'bg-accent-soft/40',
                )}
              >
                <div className="flex items-center gap-1.5 text-ink">
                  {involves && <Star className="h-3 w-3 shrink-0 text-accent" aria-label={`Involves your mission: ${involves}`} />}
                  <span className="truncate" title={involves ? `Involves your mission: ${involves}` : undefined}>
                    {event.name1 || `NORAD ${event.norad1}`}
                  </span>
                  <span className="text-ink-3">vs</span>
                  <span className="truncate">{event.name2 || `NORAD ${event.norad2}`}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 font-mono text-[10px] text-ink-2">
                  <span>{deltaSec < 0 ? 'T+' : 'T-'}{formatConjunctionCountdown(Math.abs(deltaSec))}</span>
                  <span>{Number.isFinite(event.rangeKm) ? `${event.rangeKm.toFixed(2)} km` : '--'}</span>
                  <span className={cls ? PC_TONE[cls] : 'text-ink-3'}>
                    Pc={event.pc !== null && !Number.isNaN(event.pc) ? event.pc.toExponential(1) : '--'}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
